import { Component, Input, OnInit, OnDestroy, Inject, OnChanges } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { SeoContent, HubPageSeoContent } from '~shared/models';
import { OtherSeoContent } from '~shared/models/other-seo-content.model';
import { MediaObserver } from '@angular/flex-layout';
import { takeUntil } from 'rxjs/operators';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { NEWSLETTER_PAGE_SOURCE } from '~shared/constants';
import { PlpVariantRestyleService } from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';

@Component({
  selector: 'app-custom-seo-content',
  templateUrl: './custom-seo-content.component.html',
  styleUrls: ['./custom-seo-content.component.scss'],
})
export class CustomSeoContentComponent implements OnInit, OnDestroy, OnChanges {
  @Input() source: NEWSLETTER_PAGE_SOURCE;
  private unsubscribe$ = new Subject();
  private _seoContent: SeoContent | HubPageSeoContent;
  private _otherContent: Array<OtherSeoContent>;

  public tableOfContents: Array<string>;
  public location: string;
  public displayMobile = false;
  public isMobileOrTablet = false;
  isPLP: boolean;
  isNewPLPVariantRestyle$: Observable<boolean>;

  @Input()
  set seoContent(value: SeoContent | HubPageSeoContent) {
    this._seoContent = value;
    this._otherContent = this.initializeOtherContent(value);
  }

  private instanceOfSeoContent = (object: any): object is SeoContent => 'otherContent' in object;

  get otherContent(): Array<OtherSeoContent> {
    return this._otherContent;
  }

  get seoContentInfo(): SeoContent | HubPageSeoContent {
    return this._seoContent;
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private mediaObserver: MediaObserver,
    private plpVariantRestyleService: PlpVariantRestyleService
  ) {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.isMobileOrTablet =
          this.mediaObserver.isActive(MEDIA_ALISES.mobile) || this.mediaObserver.isActive(MEDIA_ALISES.tablet);
      });
  }

  ngOnInit(): void {
    this.location = window.location.href;
    this.isNewPLPVariantRestyle$ = this.plpVariantRestyleService.isNewPLPVariantRestyle();
    this.isPLP = this.plpVariantRestyleService.isPLP();
  }

  ngOnChanges(): void {
    this.setTableOfContents();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Scroll to a particular element when clicking on the links in table of contents
   */
  scroll(el: string): void {
    const element = this.document.getElementById(el);

    if (!!element) {
      element.scrollIntoView();
    }
  }

  showOnMobile(): void {
    this.displayMobile = true;
  }

  hideOnMobile(): void {
    this.displayMobile = false;
  }

  private initializeOtherContent(value: SeoContent | HubPageSeoContent): Array<OtherSeoContent> {
    if (!value) {
      return [];
    }

    const otherContentValue = this.instanceOfSeoContent(value) ? value.otherContent : value.otherContentJSON;

    if (Array.isArray(otherContentValue)) {
      return otherContentValue;
    }

    return this.getOtherContentFromJsonString(otherContentValue);
  }

  private getOtherContentFromJsonString(value: string): Array<OtherSeoContent> {
    try {
      return JSON.parse(value);
    } catch (e) {
      return JSON.parse('[]');
    }
  }

  private setTableOfContents(): void {
    this.tableOfContents = this.otherContent
      .filter((item: any) => {
        return (item.type === 'text' && item.value.title) || item.type === 'faq';
      })
      .map((item: any) => {
        return item.value.title ? item.value.title : item.type;
      });
  }
}
